// hooks/useShipping.js
// Shipping Hook - Synchronized with Mobile App
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { apiService } from "../services/api";

export const useShippingZones = () => {
  return useQuery({
    queryKey: ["shipping-zones"],
    queryFn: async () => {
      const response = await apiService.getActiveShippingZones();
      return response.data || [];
    },
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export const useCalculateShipping = () => {
  return useMutation({
    mutationFn: (neighborhoodCode) => apiService.calculateShippingFee(neighborhoodCode),
  });
};

// Combined shipping hook for checkout
export const useShipping = () => {
  const [shippingFee, setShippingFee] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  
  const zonesQuery = useShippingZones();
  const calculateMutation = useCalculateShipping();

  const calculateShipping = useCallback(async (neighborhoodCode) => {
    setIsCalculating(true);
    try {
      if (!neighborhoodCode) {
        setShippingFee(199.99);
        setIsCalculating(false);
        return;
      }
      
      const result = await calculateMutation.mutateAsync(neighborhoodCode);
      // Match st_cael format: response.data.shippingFee
      if (result?.status === 'success' && result?.data?.shippingFee) {
        setShippingFee(result.data.shippingFee);
      } else if (result?.data?.fee || result?.fee) {
        // Fallback to alternative format
        setShippingFee(result.data?.fee || result.fee);
      } else {
        setShippingFee(199.99); // Default fee like st_cael mobile app
      }
    } catch (error) {
      // Fallback to default fee on error (like st_cael mobile app)
      setShippingFee(199.99);
    } finally {
      setIsCalculating(false);
    }
  }, [calculateMutation]);

  return {
    // Data
    shippingFee,
    shippingZones: zonesQuery.data || [],
    
    // Actions
    calculateShipping,
    
    // States
    isCalculating,
    isLoadingZones: zonesQuery.isLoading,
    error: calculateMutation.error || zonesQuery.error,
  };
};

export default useShipping;
